import React, { useState } from 'react'
import {
    ArrowDownTrayIcon,
    PencilIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import classnames from 'classnames'
import type { TariffHistoryAttachment } from '../../../apis/types'
import { downloadTariffAttachmentApi, unlinkRateSheetFromHistoryApi } from '../../../apis/apis'
import Modal from '../../../components/Modal'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { formatUTCDetailedDate } from '../../../util/string'
import { CountBadge } from './TariffFieldKey'
import { TariffDocumentViewerModal } from './TariffDocumentViewerModal'
import { TariffLinkRateSheetPopover } from './TariffLinkRateSheetPopover'
import { TariffEditRateSheetModal } from './TariffEditRateSheetModal'
import { TariffUploadRateSheetModal } from './TariffUploadRateSheetModal'

const metaBadgeClass =
    'inline-flex rounded bg-slate-100 px-1 py-0.5 text-[11px] font-medium leading-tight text-slate-600 ring-1 ring-inset ring-slate-500/15 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-400/20'

const actionButtonClass =
    'inline-flex items-center justify-center rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'

const RateSheetCard: React.FC<{
    attachment: TariffHistoryAttachment
    canEdit: boolean
    deleting: boolean
    onView: () => void
    onEdit: () => void
    onDownload: () => void
    onRemove: () => void
}> = ({ attachment, canEdit, deleting, onView, onEdit, onDownload, onRemove }) => {
    const uploadedLabel =
        attachment.uploadedAt != null
            ? formatUTCDetailedDate(new Date(attachment.uploadedAt))
            : null

    const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onView()
        }
    }

    const stopCardActivation = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation()
    }

    return (
        <article
            role="button"
            tabIndex={0}
            onClick={onView}
            onKeyDown={handleCardKeyDown}
            aria-label={`View ${attachment.label}`}
            className={classnames(
                'grid w-[300px] max-w-full min-h-[4.25rem] cursor-pointer grid-rows-[auto_minmax(1.25rem,auto)] gap-1 rounded-lg border border-slate-200 bg-white p-2',
                'transition-colors hover:border-slate-300 hover:bg-slate-50',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2',
                'dark:border-slate-600 dark:bg-slate-900 dark:hover:border-slate-500 dark:hover:bg-slate-800/80 dark:focus-visible:ring-offset-slate-900'
            )}
        >
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                <h3
                    className="min-w-0 truncate text-xs font-medium leading-4 text-slate-900 dark:text-slate-100"
                    title={attachment.label}
                >
                    {attachment.label}
                </h3>
                <span
                    className={classnames(
                        'inline-flex h-4 w-11 shrink-0 items-center justify-center rounded-full bg-sky-100 px-1 text-[9px] font-semibold uppercase tracking-wide text-sky-800 dark:bg-sky-950/50 dark:text-sky-200',
                        !attachment.isShared && 'invisible'
                    )}
                    aria-hidden={!attachment.isShared}
                >
                    Shared
                </span>
            </div>
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                    {attachment.category === 'GROUP' ? (
                        <span className={classnames(metaBadgeClass, 'shrink-0')}>Group</span>
                    ) : null}
                    {attachment.vintageYear != null && attachment.vintageYear !== '' ? (
                        <span className={classnames(metaBadgeClass, 'shrink-0')}>
                            PCIA {attachment.vintageYear}
                        </span>
                    ) : null}
                    {uploadedLabel != null ? (
                        <span className="min-w-0 truncate text-[11px] leading-4 text-slate-400 dark:text-slate-400">
                            {uploadedLabel}
                        </span>
                    ) : null}
                </div>
                <div className="flex shrink-0 items-center justify-end gap-0.5">
                    <button
                        type="button"
                        onClick={(event) => {
                            stopCardActivation(event)
                            onDownload()
                        }}
                        className={actionButtonClass}
                        aria-label={`Download ${attachment.label}`}
                        title="Download"
                    >
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    </button>
                    {canEdit ? (
                        <button
                            type="button"
                            onClick={(event) => {
                                stopCardActivation(event)
                                onEdit()
                            }}
                            className={actionButtonClass}
                            aria-label={`Edit ${attachment.label}`}
                            title="Edit"
                        >
                            <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                    {canEdit ? (
                        <button
                            type="button"
                            disabled={deleting}
                            onClick={(event) => {
                                stopCardActivation(event)
                                onRemove()
                            }}
                            className={classnames(
                                actionButtonClass,
                                'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-300'
                            )}
                            aria-label={`Remove ${attachment.label} from rateset`}
                            title="Remove"
                        >
                            <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </div>
            </div>
        </article>
    )
}

export const TariffRateDocuments: React.FC<{
    historyCuid: string
    utilityCuid: string | null
    attachments: TariffHistoryAttachment[]
    canEdit: boolean
    onAttachmentsChange: (attachments: TariffHistoryAttachment[]) => void
}> = ({ historyCuid, utilityCuid, attachments, canEdit, onAttachmentsChange }) => {
    const dispatch = useAppDispatch()
    const [viewerAttachment, setViewerAttachment] = useState<TariffHistoryAttachment | null>(null)
    const [uploadOpen, setUploadOpen] = useState(false)
    const [editingAttachment, setEditingAttachment] = useState<TariffHistoryAttachment | null>(null)
    const [deletingCuid, setDeletingCuid] = useState<string | null>(null)
    const [confirmModal, setConfirmModal] = useState<React.ReactElement | null>(null)

    const handleDownload = (attachment: TariffHistoryAttachment): void => {
        void downloadTariffAttachmentApi(attachment.cuid, 'attachment')
            .then(({ data, filename, contentType }) => {
                const blob = new Blob([data], { type: contentType ?? 'application/pdf' })
                const url = URL.createObjectURL(blob)
                const anchor = document.createElement('a')
                anchor.href = url
                anchor.download = filename
                document.body.appendChild(anchor)
                anchor.click()
                anchor.remove()
                URL.revokeObjectURL(url)
            })
            .catch(() => {
                void dispatch(pushToast({ type: 'error', message: 'Failed to download rate sheet' }))
            })
    }

    const handleUnlink = async (attachmentCuid: string): Promise<void> => {
        setDeletingCuid(attachmentCuid)
        try {
            await unlinkRateSheetFromHistoryApi(historyCuid, attachmentCuid)
            onAttachmentsChange(attachments.filter((a) => a.cuid !== attachmentCuid))
            void dispatch(pushToast({ type: 'success', message: 'Rate sheet removed from this rateset' }))
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to remove rate sheet from rateset' }))
        } finally {
            setDeletingCuid(null)
        }
    }

    const requestUnlink = async (attachment: TariffHistoryAttachment): Promise<void> => {
        try {
            await new Promise((resolve, reject) => {
                setConfirmModal(
                    <Modal
                        resolve={resolve}
                        reject={reject}
                        title="Remove from this rateset?"
                        message={
                            attachment.isShared
                                ? `"${attachment.label}" stays in the shared library but will no longer appear on this rateset.`
                                : `Remove "${attachment.label}" from this rateset?`
                        }
                    />
                )
            })
        } catch {
            setConfirmModal(null)
            return
        }
        setConfirmModal(null)
        await handleUnlink(attachment.cuid)
    }

    return (
        <div className="min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex min-w-0 items-center gap-1.5 py-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <span>Rate sheets</span>
                    <CountBadge count={attachments.length} tone="slate" />
                </div>
                {canEdit ? (
                    <div className="flex flex-wrap gap-2">
                        <TariffLinkRateSheetPopover
                            utilityCuid={utilityCuid}
                            historyCuid={historyCuid}
                            linkedCuids={attachments.map((a) => a.cuid)}
                            onLinked={(attachment) => {
                                if (!attachments.some((a) => a.cuid === attachment.cuid)) {
                                    onAttachmentsChange([...attachments, attachment])
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => setUploadOpen(true)}
                            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                        >
                            Upload rate sheet
                        </button>
                    </div>
                ) : null}
            </div>

            {attachments.length === 0 ? (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    No rate sheet documents.
                </p>
            ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((attachment) => (
                        <RateSheetCard
                            key={attachment.cuid}
                            attachment={attachment}
                            canEdit={canEdit}
                            deleting={deletingCuid === attachment.cuid}
                            onView={() => setViewerAttachment(attachment)}
                            onEdit={() => setEditingAttachment(attachment)}
                            onDownload={() => handleDownload(attachment)}
                            onRemove={() => void requestUnlink(attachment)}
                        />
                    ))}
                </div>
            )}

            <TariffDocumentViewerModal
                attachment={viewerAttachment}
                onClose={() => setViewerAttachment(null)}
            />
            <TariffUploadRateSheetModal
                open={uploadOpen}
                historyCuid={historyCuid}
                onClose={() => setUploadOpen(false)}
                onUploaded={(attachment) => onAttachmentsChange([...attachments, attachment])}
            />
            <TariffEditRateSheetModal
                open={editingAttachment != null}
                attachment={editingAttachment}
                onClose={() => setEditingAttachment(null)}
                onSaved={(updated) => {
                    onAttachmentsChange(
                        attachments.map((attachment) =>
                            attachment.cuid === updated.cuid ? updated : attachment
                        )
                    )
                }}
            />
            {confirmModal}
        </div>
    )
}
